import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TextField,
  Button,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { useRouter } from "../../../hooks/useRouter";
import { formatCurrency } from "../../../utils";
import FinanceContainer from "../containers/FinanceContainer";
import config from "../../../config";

interface AgeAnalysisRow {
  clientId: string;
  clientName: string;
  unitRef?: string;
  development?: string;
  Current: number;
  "30 Days": number;
  "60 Days": number;
  "90 Days": number;
  "120+": number;
  total: number;
  balanceLedger?: number;
}

interface AgeAnalysisData {
  asAt: string;
  bands: string[];
  rows: AgeAnalysisRow[];
  totals: AgeAnalysisRow;
}

const DebtorsAgeAnalysis: React.FC = () => {
  const router = useRouter();
  const [data, setData] = useState<AgeAnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [asAtDate, setAsAtDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [hideZeroBalance, setHideZeroBalance] = useState(true);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [agingInProgress, setAgingInProgress] = useState(false);
  const [ageActionMessage, setAgeActionMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    fetchAgeAnalysis();
  }, [hideZeroBalance]);

  const fetchAgeAnalysis = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vms_access_token");
      const url = `${config.API_BASE_URL}/debtors/age-analysis?asAtDate=${asAtDate}&hideZeroBalance=${hideZeroBalance}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching age analysis:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchAgeAnalysis();
  };

  const handleToggleRow = (clientId: string) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedRows(newSelected);
    console.log("[AGE_ANALYSIS] Selection updated:", Array.from(newSelected));
  };

  const handleSelectAll = () => {
    if (!data) return;
    if (selectedRows.size === data.rows.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(data.rows.map((row) => row.clientId)));
    }
  };

  const handleClearSelection = () => {
    setSelectedRows(new Set());
    setAgeActionMessage(null);
  };

  const handleAgeDebtors = async (debtorIds?: string[]) => {
    if (!data) return;

    const idsToAge = debtorIds || Array.from(selectedRows);
    if (idsToAge.length === 0) return;

    console.log("[AGE_ANALYSIS] Aging debtors with IDs:", idsToAge);
    console.log("[AGE_ANALYSIS] Number of debtors to age:", idsToAge.length);

    try {
      setAgingInProgress(true);
      const token = localStorage.getItem("vms_access_token");
      const requestBody = { debtorIds: idsToAge };
      console.log("[AGE_ANALYSIS] Request body:", requestBody);

      const response = await fetch(`${config.API_BASE_URL}/debtors/bulk-age`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();
      console.log("[AGE_ANALYSIS] Response:", responseData);

      if (response.ok) {
        setAgeActionMessage({
          type: "success",
          text: `Successfully marked ${idsToAge.length} debtor(s) as aged.`,
        });
        setSelectedRows(new Set());
        setTimeout(() => {
          fetchAgeAnalysis();
          setAgeActionMessage(null);
        }, 800);
      } else {
        throw new Error(responseData.message || "Failed to age debtors");
      }
    } catch (error) {
      console.error("[AGE_ANALYSIS] Error aging debtors:", error);
      setAgeActionMessage({
        type: "error",
        text: `Error aging debtors: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      });
    } finally {
      setAgingInProgress(false);
    }
  };

  const handleExport = () => {
    if (!data) return;

    const headers = [
      "Client Name",
      "Unit Ref",
      "Development",
      "Current",
      "30 Days",
      "60 Days",
      "90 Days",
      "120+",
      "Total",
    ];
    const csvRows = [
      headers.join(","),
      ...data.rows.map((row) =>
        [
          `"${row.clientName}"`,
          `"${row.unitRef || ""}"`,
          `"${row.development || ""}"`,
          row.Current,
          row["30 Days"],
          row["60 Days"],
          row["90 Days"],
          row["120+"],
          row.total,
        ].join(","),
      ),
      [
        "TOTAL",
        "",
        "",
        data.totals.Current,
        data.totals["30 Days"],
        data.totals["60 Days"],
        data.totals["90 Days"],
        data.totals["120+"],
        data.totals.total,
      ].join(","),
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `debtors-age-analysis-${asAtDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getCellColor = (value: number, band: string) => {
    if (value === 0) return "inherit";
    if (band === "120+") return "#ffebee";
    if (band === "90 Days") return "#fff3e0";
    if (band === "60 Days") return "#fffde7";
    if (band === "30 Days") return "#e3f2fd";
    return "#e8f5e9";
  };

  const summaryCards = data
    ? [
        {
          title: "Current (0-30 days)",
          value: data.totals.Current,
          color: "#4caf50",
        },
        {
          title: "30 Days (31-60)",
          value: data.totals["30 Days"],
          color: "#2196f3",
        },
        {
          title: "60 Days (61-90)",
          value: data.totals["60 Days"],
          color: "#ff9800",
        },
        {
          title: "90 Days (91-120)",
          value: data.totals["90 Days"],
          color: "#f44336",
        },
        { title: "120+ Days", value: data.totals["120+"], color: "#9c27b0" },
        {
          title: "Total Outstanding",
          value: data.totals.total,
          color: "#1976d2",
        },
      ]
    : [];

  return (
    <FinanceContainer>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <IconButton onClick={() => router.push("/finance/debtors")}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">Debtors Age Analysis</Typography>
        </Box>

        {/* Controls */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            type="date"
            label="As at Date"
            value={asAtDate}
            onChange={(e) => setAsAtDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={!data}
          >
            Export CSV
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                checked={hideZeroBalance}
                onChange={(e) => setHideZeroBalance(e.target.checked)}
              />
            }
            label="Hide zero balances"
          />
        </Box>

        {/* Selection & Aging Actions */}
        {data && data.rows.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 3,
              alignItems: "center",
              flexWrap: "wrap",
              backgroundColor:
                selectedRows.size > 0 ? "#f5f5f5" : "transparent",
              padding: selectedRows.size > 0 ? 2 : 0,
              borderRadius: 1,
            }}
          >
            {selectedRows.size > 0 && (
              <>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {selectedRows.size} debtor(s) selected
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<FlagIcon />}
                  onClick={() => handleAgeDebtors()}
                  disabled={agingInProgress}
                >
                  Age Selected ({selectedRows.size})
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleSelectAll}
                  disabled={agingInProgress}
                >
                  {selectedRows.size === data.rows.length
                    ? "Clear All"
                    : "Select All"}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleClearSelection}
                  disabled={agingInProgress}
                >
                  Clear Selection
                </Button>
              </>
            )}
            {selectedRows.size === 0 && data.rows.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<FlagIcon />}
                onClick={() =>
                  handleAgeDebtors(data.rows.map((r) => r.clientId))
                }
                disabled={agingInProgress}
              >
                Age All Debtors
              </Button>
            )}
          </Box>
        )}

        {/* Message Display */}
        {ageActionMessage && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor:
                ageActionMessage.type === "success" ? "#e8f5e9" : "#ffebee",
              border: `1px solid ${
                ageActionMessage.type === "success" ? "#4caf50" : "#f44336"
              }`,
              borderRadius: 1,
              color:
                ageActionMessage.type === "success" ? "#2e7d32" : "#c62828",
            }}
          >
            {ageActionMessage.text}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : data ? (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {summaryCards.map((card, index) => (
                <Grid item xs={12} sm={6} md={2} key={index}>
                  <Card sx={{ backgroundColor: card.color, color: "white" }}>
                    <CardContent sx={{ textAlign: "center", py: 1 }}>
                      <Typography variant="caption">{card.title}</Typography>
                      <Typography variant="h6">
                        {formatCurrency(card.value)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* As At Date Display */}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Report as at: {data.asAt}
            </Typography>

            {/* Age Analysis Table */}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#1976d2" }}>
                    <TableCell
                      sx={{ color: "white", fontWeight: "bold", width: 50 }}
                    >
                      <Checkbox
                        checked={
                          selectedRows.size === data.rows.length &&
                          data.rows.length > 0
                        }
                        indeterminate={
                          selectedRows.size > 0 &&
                          selectedRows.size < data.rows.length
                        }
                        onChange={handleSelectAll}
                        sx={{ color: "white" }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Client Name
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Unit Ref
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Development
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      Current
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      30 Days
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      60 Days
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      90 Days
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      120+ Days
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No outstanding amounts
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {data.rows.map((row) => (
                        <TableRow
                          key={row.clientId}
                          hover
                          sx={{
                            backgroundColor: selectedRows.has(row.clientId)
                              ? "#e3f2fd"
                              : "inherit",
                            cursor: "pointer",
                          }}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedRows.has(row.clientId)}
                              onChange={() => handleToggleRow(row.clientId)}
                            />
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              router.push(`/finance/debtors/${row.clientId}`)
                            }
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography>{row.clientName}</Typography>
                              {(row as any).markedForAging && (
                                <Chip
                                  label="Aged"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{ height: 24 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              router.push(`/finance/debtors/${row.clientId}`)
                            }
                          >
                            {row.unitRef || "-"}
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              router.push(`/finance/debtors/${row.clientId}`)
                            }
                          >
                            {row.development || "-"}
                          </TableCell>
                          <TableCell
                            align="right"
                            onClick={() =>
                              router.push(`/finance/debtors/${row.clientId}`)
                            }
                            sx={{
                              backgroundColor: getCellColor(
                                row.Current,
                                "Current",
                              ),
                            }}
                          >
                            {formatCurrency(row.Current)}
                          </TableCell>
                          <TableCell
                            align="right"
                            onClick={() =>
                              router.push(`/finance/debtors/${row.clientId}`)
                            }
                            sx={{
                              backgroundColor: getCellColor(
                                row["30 Days"],
                                "30 Days",
                              ),
                            }}
                          >
                            {formatCurrency(row["30 Days"])}
                          </TableCell>
                          <TableCell
                            align="right"
                            onClick={() =>
                              router.push(`/finance/debtors/${row.clientId}`)
                            }
                            sx={{
                              backgroundColor: getCellColor(
                                row["60 Days"],
                                "60 Days",
                              ),
                            }}
                          >
                            {formatCurrency(row["60 Days"])}
                          </TableCell>
                          <TableCell
                            align="right"
                            onClick={() =>
                              router.push(`/finance/debtors/${row.clientId}`)
                            }
                            sx={{
                              backgroundColor: getCellColor(
                                row["90 Days"],
                                "90 Days",
                              ),
                            }}
                          >
                            {formatCurrency(row["90 Days"])}
                          </TableCell>
                          <TableCell
                            align="right"
                            onClick={() =>
                              router.push(`/finance/debtors/${row.clientId}`)
                            }
                            sx={{
                              backgroundColor: getCellColor(
                                row["120+"],
                                "120+",
                              ),
                            }}
                          >
                            {formatCurrency(row["120+"])}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: "bold" }}
                            onClick={() =>
                              router.push(`/finance/debtors/${row.clientId}`)
                            }
                          >
                            {formatCurrency(row.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Totals Row */}
                      <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                        <TableCell colSpan={4} sx={{ fontWeight: "bold" }}>
                          TOTAL
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(data.totals.Current)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(data.totals["30 Days"])}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(data.totals["60 Days"])}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(data.totals["90 Days"])}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(data.totals["120+"])}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(data.totals.total)}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Typography color="error">
            Failed to load age analysis data
          </Typography>
        )}
      </Box>
    </FinanceContainer>
  );
};

export default DebtorsAgeAnalysis;
